import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
// Maps the server-side subtype to the AssetsController asset key.
// "bowl" uses the "food" model (a food dish); all others match their folder name.
export const SUBTYPE_TO_MODEL = {
    bowl: "OBJECT_food",
    bed: "OBJECT_bed",
    toy: "OBJECT_toy",
    toilet: "OBJECT_toilet",
};
// Uniform scale applied to each instantiated model root node.
export const SUBTYPE_SCALE = {
    bowl: 2.0,
    bed: 1.0,
    toy: 2.0,
    toilet: 2.0,
};
const SUBTYPE_LABEL_HEIGHT = {
    bowl: 0.8,
    bed: 1.5,
    toy: 0.8,
    toilet: 1.2,
};
// Fallback colors used when the GLTF container is not yet in _loadedAssets
const SUBTYPE_COLORS = {
    bowl: new Color3(0.2, 0.4, 0.9),
    bed: new Color3(0.55, 0.27, 0.07),
    toy: new Color3(0.95, 0.85, 0.1),
    toilet: new Color3(0.9, 0.9, 0.9),
};
export class WorldObject extends TransformNode {
    constructor(name, scene, entity, game) {
        super(name, scene);
        this.type = "worldobject";
        this._instantiatedEntries = null;
        this._scene = scene;
        this._game = game;
        this.entity = entity;
        this.sessionId = entity.sessionId;
        Object.assign(this, entity);
        this._spawn();
    }
    _spawn() {
        var _a, _b, _c, _d, _e;
        const subtype = (_a = this.entity.subtype) !== null && _a !== void 0 ? _a : "toy";
        const modelKey = SUBTYPE_TO_MODEL[subtype];
        const container = modelKey ? this._game._loadedAssets[modelKey] : null;
        if (container) {
            this._instantiatedEntries = container.instantiateModelsToScene((name) => `${name}_${this.sessionId}`);
            const root = this._instantiatedEntries.rootNodes[0];
            if (root) {
                root.parent = this;
                root.position.setAll(0);
                root.scaling.setAll((_b = SUBTYPE_SCALE[subtype]) !== null && _b !== void 0 ? _b : 1.0);
                const childMeshes = root.getChildMeshes(true);
                for (const m of childMeshes) {
                    m.isPickable = false;
                }
                if (childMeshes.length > 0) {
                    this.mesh = childMeshes[0];
                }
            }
        }
        else {
            // fallback: colored cube
            const color = (_c = SUBTYPE_COLORS[subtype]) !== null && _c !== void 0 ? _c : Color3.Gray();
            this.mesh = MeshBuilder.CreateBox(`worldobj_${this.sessionId}`, { size: 0.6 }, this._scene);
            this.mesh.parent = this;
            this.mesh.position.y = 0.3;
            this.mesh.isPickable = false;
            const mat = new StandardMaterial(`worldobj_mat_${this.sessionId}`, this._scene);
            mat.diffuseColor = color;
            mat.specularColor = Color3.Black();
            this.mesh.material = mat;
        }
        const labelHeight = (_d = SUBTYPE_LABEL_HEIGHT[subtype]) !== null && _d !== void 0 ? _d : 1.0;
        this._addLabel((_e = this.entity.name) !== null && _e !== void 0 ? _e : subtype, labelHeight);
        this.setPosition();
        this.entity.onChange(() => {
            Object.assign(this, this.entity);
            this.setPosition();
        });
    }
    _addLabel(text, yOffset = 1.0) {
        const fontSize = 48;
        const font = `bold ${fontSize}px Arial`;
        const tmp = new DynamicTexture("_tmp_measure", 64, this._scene);
        const ctx = tmp.getContext();
        ctx.font = font;
        const textWidth = ctx.measureText(text).width + 16;
        tmp.dispose();
        const texHeight = fontSize * 1.5;
        const planeHeight = 0.35;
        const planeWidth = textWidth * (planeHeight / texHeight);
        const texture = new DynamicTexture(`worldobj_tex_${this.sessionId}`, { width: textWidth, height: texHeight }, this._scene);
        texture.drawText(text, null, null, font, "#FFFFFF", "transparent", true);
        const mat = new StandardMaterial(`worldobj_label_mat_${this.sessionId}`, this._scene);
        mat.diffuseTexture = texture;
        mat.opacityTexture = texture;
        mat.disableLighting = true;
        mat.emissiveColor = Color3.White();
        const plane = MeshBuilder.CreatePlane(`worldobj_label_${this.sessionId}`, { width: planeWidth, height: planeHeight, sideOrientation: Mesh.DOUBLESIDE }, this._scene);
        plane.parent = this;
        plane.position.y = yOffset;
        plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
        plane.material = mat;
        plane.isPickable = false;
    }
    update(_delta) { }
    updateServerRate(_delta) { }
    updateSlowRate(_delta) { }
    lod(_currentPlayer) { }
    setPosition() {
        this.position = this.getPosition();
    }
    getPosition() {
        return new Vector3(this.x, this.y, this.z);
    }
    remove() {
        var _a;
        (_a = this._instantiatedEntries) === null || _a === void 0 ? void 0 : _a.dispose();
        this.dispose();
    }
}
